import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import {
	StyledBackground,
	StyledBlueBg,
	StyledContainer,
	StyledMaxButton,
	StyledSlider,
	StyledSliderBackgroundWrapper,
	StyledSliderContainer,
	StyledValueContainer,
} from './СountSlider.styled';

interface IFuelSliderProps {
	min: number;
	max: number;
	sliderValue: number;
	count: number;
	onChange: (newValue: number) => void;
	disabled?: boolean;
}

const СountSlider = ({
	min,
	max,
	sliderValue,
	count,
	onChange,
	disabled = false,
}: IFuelSliderProps) => {
	const [value, setValue] = useState(min);
	const sliderRef = useRef(null);

	const getBackgroundPercentage = useCallback(
		(currentValue: number) => {
			if (max === min) return 0;
			return ((currentValue - min) / (max - min)) * 100;
		},
		[min, max],
	);

	const gradient = useMemo(() => {
		if (min === 0) {
			return '#ffffff 0%,';
		}
		if (min === max) {
			return `#2d70da 0%,
						 #2d70da 100%,`;
		}
		return `#2d70da 0%,
						 #2d70da 5px,
						 #ffffff 5px,`;
	}, [min, max]);

	const onSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;

		const newValue = Number(e.target.value);
		setValue(newValue);

		const percentage = getBackgroundPercentage(newValue);
		e.target.style.background = `linear-gradient(
			to right,
			${gradient}
			#ffffff calc(9px + (100% - 20px)*${percentage / 100}),
			transparent calc(9px + (100% - 20px)*${percentage / 100}),
			transparent 100%
		)`;

		onChange(newValue);
	};

	const handleMaxGradient = useCallback(() => {
		if (sliderRef?.current) {
			const percentage = getBackgroundPercentage(max);
			(sliderRef?.current as any).style.background = `linear-gradient(
				to right,
				${gradient}
				#ffffff calc(9px + (100% - 20px)*${percentage / 100}),
				transparent calc(9px + (100% - 20px)*${percentage / 100}),
				transparent 100%
			)`;
		}
	}, [sliderRef, gradient, getBackgroundPercentage, max]);

	const handleMax = useCallback(() => {
		if (disabled) return;

		handleMaxGradient();
		onChange(max);
		setValue(max);
	}, [max, onChange, handleMaxGradient, disabled]);

	useEffect(() => {
		if (sliderValue >= min && sliderValue <= max) {
			setValue(sliderValue);
			if (sliderRef?.current) {
				const percentage = getBackgroundPercentage(sliderValue);
				(sliderRef?.current as any).style.background = `linear-gradient(
					to right,
					${gradient}
					#ffffff calc(9px + (100% - 20px)*${percentage / 100}),
					transparent calc(9px + (100% - 20px)*${percentage / 100}),
					transparent 100%
				)`;
			}
		}
	}, [sliderValue, min, max, gradient, getBackgroundPercentage]);

	const blueBgWidth = useMemo(() => {
		if (min === 0) return 0;
		if (min === max) return 330;
		return (min / max) * 290 + 25;
	}, [min, max]);

	const sliderContainerWidth = useMemo(() => {
		if (max === min) return 0;
		return 100 - (min / max) * 100;
	}, [min, max]);

	return (
		<StyledSliderBackgroundWrapper>
			<StyledBackground />
			{min > 0 && (
				<StyledBlueBg
					style={{
						width: `${blueBgWidth}px`,
					}}
				/>
			)}
			<StyledContainer>
				<StyledSliderContainer>
					<div
						style={{
							width: `${max - min !== 0 ? (min / max) * 100 : 97}%`,
						}}
					/>
					<StyledSlider
						ref={sliderRef}
						value={value}
						onChange={onSlide}
						min={min}
						max={max}
						step={1}
						disabled={disabled}
						style={{
							width: `${sliderContainerWidth}%`,
						}}
					/>
				</StyledSliderContainer>
			</StyledContainer>
			<StyledMaxButton
				onClick={() => {
					handleMax();
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				tabIndex={-1}
			>
				Max
			</StyledMaxButton>
			<StyledValueContainer>
				<span style={{ fontWeight: 500 }}>{count}</span>
				&nbsp;
				<span style={{ opacity: 0.5 }}>шт.</span>
			</StyledValueContainer>
		</StyledSliderBackgroundWrapper>
	);
};

export { СountSlider };
